/** Service catalog — SEO landing pages + featured mega-menu pillars */

import { specialtyServices } from "@/lib/data/specialty-services";

export type ServiceFaq = { question: string; answer: string };

export type Service = {
  id: string;
  number: string;
  title: string;
  slug: string;
  href: string;
  short: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  capabilities: string[];
  accent: string;
  featured: boolean;
  problem: { title: string; body: string };
  opportunity: { title: string; body: string };
  outcomes: string[];
  faqs: ServiceFaq[];
};

export const services: Service[] = [
  {
    id: "ai",
    number: "01",
    title: "Artificial Intelligence & Machine Learning",
    slug: "artificial-intelligence",
    href: "/services/artificial-intelligence",
    short:
      "From copilots and RAG systems to agentic workflows — intelligence engineered for production.",
    description:
      "Design, build, and operationalize enterprise AI — generative systems, agentic automation, ML platforms, and governed model integration.",
    seoTitle: "AI & Machine Learning Development Company",
    seoDescription:
      "Hire Maxridge for generative AI, LLM apps, RAG systems, AI agents, and machine learning engineering — production AI systems for startups and enterprises.",
    keywords: [
      "AI development company",
      "AI development company India",
      "generative AI development company",
      "machine learning engineering",
      "AI agent development company",
      "RAG development company",
      "LLM application development",
      "enterprise AI development",
      "hire AI engineers India",
    ],
    capabilities: [
      "Generative AI",
      "Agentic AI",
      "AI Agents",
      "LLM Applications",
      "RAG Systems",
      "Machine Learning",
      "NLP",
      "Computer Vision",
      "AI Automation",
      "AI Strategy",
      "Model Integration",
      "AI Governance",
    ],
    accent: "from-violet-500/30 to-cyan-400/20",
    featured: true,
    problem: {
      title: "AI experiments that never reach production.",
      body: "Many teams ship demos that fail on real data, lack governance, or never integrate with core business systems.",
    },
    opportunity: {
      title: "Ship intelligence that does the work.",
      body: "We engineer copilots, agents, RAG platforms, and ML systems with evaluation, security, and operational readiness built in.",
    },
    outcomes: [
      "Production LLM and RAG applications",
      "Agentic workflows across enterprise tools",
      "Governed model integration with audit trails",
      "Faster decision cycles with measurable ROI",
    ],
    faqs: [
      {
        question: "Do you build production AI systems or only prototypes?",
        answer:
          "We build for production — with evaluation, observability, security, governance, and integration into existing enterprise systems.",
      },
      {
        question: "Which AI models and frameworks do you support?",
        answer:
          "We work with OpenAI, Anthropic, Gemini, open-source LLMs, LangChain/LangGraph, vector databases, and custom ML stacks based on your constraints.",
      },
      {
        question: "Can AI agents connect to our CRM, ERP, and internal APIs?",
        answer:
          "Yes. Tool-using agents and orchestration layers are designed to operate across APIs, databases, documents, and business systems under policy controls.",
      },
    ],
  },
  {
    id: "data",
    number: "02",
    title: "Data Engineering & Analytics",
    slug: "data-engineering",
    href: "/services/data-engineering",
    short:
      "Trusted pipelines and platforms that turn raw enterprise data into decision-ready intelligence.",
    description:
      "Build AI-ready data foundations — lakes, warehouses, real-time streams, governance, and analytics that fuel intelligent systems.",
    seoTitle: "Data Engineering & Analytics Services",
    seoDescription:
      "Maxridge data engineering services: pipelines, lakehouses, warehouses, real-time streams, BI, and AI-ready data platforms for reliable enterprise decisions.",
    keywords: [
      "data engineering services",
      "data pipeline development",
      "data warehouse consulting",
      "AI-ready data platform",
      "ETL ELT development",
    ],
    capabilities: [
      "Data Platforms",
      "Data Engineering",
      "Data Warehousing",
      "Data Lakes",
      "ETL / ELT",
      "Real-Time Data",
      "Business Intelligence",
      "Predictive Analytics",
      "Data Governance",
      "AI-ready Data",
    ],
    accent: "from-cyan-400/30 to-blue-500/20",
    featured: true,
    problem: {
      title: "Decisions stall when data is siloed or late.",
      body: "Fragmented sources, weak governance, and brittle pipelines block analytics and AI adoption.",
    },
    opportunity: {
      title: "Build a trusted, AI-ready data foundation.",
      body: "We design pipelines, lakehouses, and analytics layers that keep data fresh, governed, and usable for products and agents.",
    },
    outcomes: [
      "Reliable batch and streaming pipelines",
      "Unified analytics and BI foundations",
      "Governed data ready for AI workloads",
      "Lower operational cost from cleaner data ops",
    ],
    faqs: [
      {
        question: "Which data platforms do you work with?",
        answer:
          "PostgreSQL, MongoDB, Snowflake, Databricks, Kafka, Spark, and major cloud data services across AWS, Azure, and Google Cloud.",
      },
      {
        question: "Can you prepare data specifically for RAG and ML?",
        answer:
          "Yes. We design AI-ready pipelines including cleaning, chunking strategies, embeddings workflows, feature stores, and quality checks.",
      },
      {
        question: "Do you support real-time data use cases?",
        answer:
          "Yes. We build event-driven and streaming architectures for operational intelligence, alerts, and low-latency analytics.",
      },
    ],
  },
  {
    id: "cloud",
    number: "03",
    title: "Cloud & DevOps Solutions",
    slug: "cloud-devops",
    href: "/services/cloud-devops",
    short:
      "Secure, scalable cloud foundations with modern delivery, observability, and optimization.",
    description:
      "Migrate, modernize, and operate cloud-native platforms across AWS, Azure, and Google Cloud with DevSecOps discipline.",
    seoTitle: "Cloud Migration & DevOps Consulting Services",
    seoDescription:
      "Cloud & DevOps services by Maxridge: AWS, Azure, GCP migration, Kubernetes, CI/CD, IaC, DevSecOps, and observability for scalable production platforms.",
    keywords: [
      "cloud migration services",
      "DevOps consulting",
      "Kubernetes consulting",
      "CI/CD automation",
      "AWS Azure GCP development",
    ],
    capabilities: [
      "AWS",
      "Azure",
      "Google Cloud",
      "Kubernetes",
      "Docker",
      "CI/CD",
      "Infrastructure as Code",
      "Cloud Migration",
      "Cloud Native Development",
      "DevSecOps",
      "Observability",
      "Cloud Optimization",
    ],
    accent: "from-blue-500/30 to-violet-500/20",
    featured: true,
    problem: {
      title: "Cloud spend rises while delivery stays fragile.",
      body: "Manual releases, unclear ownership, and poorly designed platforms slow teams and increase risk.",
    },
    opportunity: {
      title: "Operate cloud platforms that ship safely every week.",
      body: "We modernize infrastructure with IaC, CI/CD, security gates, and observability so products scale without ops chaos.",
    },
    outcomes: [
      "Faster, safer release cycles",
      "Lower cloud waste through optimization",
      "Stronger security and compliance posture",
      "Platform reliability for product growth",
    ],
    faqs: [
      {
        question: "Do you support multi-cloud environments?",
        answer:
          "Yes. We design and operate workloads across AWS, Azure, and Google Cloud based on your architecture and commercial constraints.",
      },
      {
        question: "Can you migrate legacy systems with minimal downtime?",
        answer:
          "Yes. We use incremental migration patterns, dual-running strategies, and rigorous cutover plans to reduce business disruption.",
      },
      {
        question: "What DevOps tooling do you typically use?",
        answer:
          "Docker, Kubernetes, Terraform, GitHub Actions, ArgoCD, and cloud-native observability stacks tailored to your environment.",
      },
    ],
  },
  {
    id: "software",
    number: "04",
    title: "Enterprise Software Development",
    slug: "enterprise-software",
    href: "/services/enterprise-software",
    short:
      "Product-grade software — platforms, APIs, and applications built for scale and longevity.",
    description:
      "Engineer custom software, SaaS platforms, microservices, and modernization programs that become durable business systems.",
    seoTitle: "Enterprise Software Development Company",
    seoDescription:
      "Enterprise software development by Maxridge: custom platforms, SaaS products, APIs, microservices, and application modernization for scalable business systems.",
    keywords: [
      "enterprise software development",
      "custom software development",
      "SaaS development company",
      "application modernization",
      "microservices development",
    ],
    capabilities: [
      "Custom Software",
      "Web Applications",
      "Mobile Applications",
      "SaaS Platforms",
      "API Development",
      "Microservices",
      "Enterprise Applications",
      "Modernization",
      "Application Integration",
      "Quality Engineering",
    ],
    accent: "from-fuchsia-500/20 to-violet-500/25",
    featured: true,
    problem: {
      title: "Software that records work but does not accelerate it.",
      body: "Legacy apps and fragmented tools create slow releases, high maintenance cost, and poor user adoption.",
    },
    opportunity: {
      title: "Build product-grade systems that endure.",
      body: "We engineer enterprise applications and SaaS platforms with clean architecture, APIs, and quality gates for long-term ownership.",
    },
    outcomes: [
      "Faster feature delivery with clear architecture",
      "Lower maintenance and technical debt",
      "Better UX for internal and customer-facing users",
      "Integrations that connect your enterprise stack",
    ],
    faqs: [
      {
        question: "Do you build greenfield products and modernize existing systems?",
        answer:
          "Yes. We deliver new platforms and incremental modernization programs, including strangler patterns and API-led upgrades.",
      },
      {
        question: "What engagement models do you offer?",
        answer:
          "Project-based delivery, dedicated product squads, and long-term engineering partnerships with sprint cadence and shared KPIs.",
      },
      {
        question: "How do you ensure quality?",
        answer:
          "Automated testing, code review, CI quality gates, observability, and production readiness checklists are part of every engagement.",
      },
    ],
  },
  {
    id: "consulting",
    number: "05",
    title: "IT Consulting & Digital Transformation",
    slug: "it-consulting",
    href: "/services/it-consulting",
    short:
      "Strategy that connects technology choices to measurable enterprise outcomes.",
    description:
      "Guide AI, cloud, and digital transformation with architecture, roadmaps, and delivery models built for executive clarity.",
    seoTitle: "IT Consulting & Digital Transformation Services",
    seoDescription:
      "IT consulting and digital transformation by Maxridge: technology strategy, solution architecture, AI roadmaps, and execution plans for enterprise outcomes.",
    keywords: [
      "IT consulting company",
      "digital transformation services",
      "AI strategy consulting",
      "solution architecture consulting",
      "technology roadmap",
    ],
    capabilities: [
      "Technology Strategy",
      "Digital Transformation",
      "Enterprise Architecture",
      "AI Transformation",
      "Cloud Transformation",
      "Technology Roadmaps",
      "Process Automation",
      "Product Engineering",
      "Technology Consulting",
    ],
    accent: "from-emerald-400/20 to-cyan-400/20",
    featured: true,
    problem: {
      title: "Technology choices without an execution path.",
      body: "Teams accumulate tools and initiatives without a clear architecture, ROI story, or delivery model.",
    },
    opportunity: {
      title: "Align strategy, architecture, and delivery.",
      body: "We help leaders choose the right bets, sequence transformation, and stand up teams that ship measurable outcomes.",
    },
    outcomes: [
      "Executive-ready technology roadmaps",
      "Clear build-vs-buy and architecture decisions",
      "Transformation programs with delivery ownership",
      "Reduced waste from unfocused initiatives",
    ],
    faqs: [
      {
        question: "Do you only advise, or also implement?",
        answer:
          "Both. We provide strategy and architecture, then can execute with dedicated engineering teams when you want one accountable partner.",
      },
      {
        question: "How long is a typical discovery engagement?",
        answer:
          "Most discovery sprints run 1–3 weeks depending on scope, stakeholders, and existing system complexity.",
      },
      {
        question: "Can you help with AI transformation specifically?",
        answer:
          "Yes. We assess readiness, prioritize use cases, define governance, and plan production delivery for AI initiatives.",
      },
    ],
  },
  {
    id: "emerging",
    number: "06",
    title: "Emerging Technologies",
    slug: "emerging-technologies",
    href: "/services/emerging-technologies",
    short:
      "Explore and industrialize the next wave — agents, twins, edge, and intelligent automation.",
    description:
      "Pilot and scale emerging capabilities where they create durable advantage — not novelty for its own sake.",
    seoTitle: "Emerging Technology & Intelligent Automation Services",
    seoDescription:
      "Emerging technology services by Maxridge: agentic AI, digital twins, IoT, edge computing, blockchain, and intelligent automation for real business advantage.",
    keywords: [
      "intelligent automation services",
      "digital twin development",
      "IoT development company",
      "agentic AI solutions",
      "emerging technology consulting",
    ],
    capabilities: [
      "Agentic AI",
      "Generative AI",
      "Digital Twins",
      "Edge Computing",
      "Blockchain",
      "IoT",
      "Intelligent Automation",
      "Spatial Computing",
      "Robotics",
      "Next-generation platforms",
    ],
    accent: "from-orange-400/20 to-violet-500/25",
    featured: true,
    problem: {
      title: "Novelty projects that never become advantage.",
      body: "Emerging tech pilots often stay disconnected from operations, data, and production constraints.",
    },
    opportunity: {
      title: "Industrialize what creates durable leverage.",
      body: "We pilot carefully, measure outcomes, and scale only the capabilities that improve products, operations, or customer experience.",
    },
    outcomes: [
      "Focused pilots with clear success metrics",
      "Production paths for validated capabilities",
      "Reduced risk from hype-driven spending",
      "Differentiated product and operations advantages",
    ],
    faqs: [
      {
        question: "How do you decide what emerging tech is worth pursuing?",
        answer:
          "We evaluate business impact, data readiness, integration cost, and operational ownership before recommending a pilot.",
      },
      {
        question: "Can you move from pilot to production?",
        answer:
          "Yes. Successful pilots are hardened with security, monitoring, scale testing, and handover into your operating model.",
      },
      {
        question: "Do you support IoT and edge use cases?",
        answer:
          "Yes. We design edge and IoT architectures when they create clear operational or product value.",
      },
    ],
  },
  {
    id: "custom",
    number: "07",
    title: "Custom Software Development",
    slug: "custom-software-development",
    href: "/services/custom-software-development",
    short:
      "Bespoke software built around your workflows — not generic templates that force compromise.",
    description:
      "End-to-end custom software development for web, mobile, cloud, and enterprise platforms — designed for your processes, integrations, and growth plans.",
    seoTitle: "Custom Software Development Company",
    seoDescription:
      "Custom software development company Maxridge builds reliable web, mobile, and enterprise systems tailored to your workflows — with AI-ready architecture and predictable delivery.",
    keywords: [
      "custom software development company",
      "custom software development services",
      "bespoke software development",
      "software development company India",
      "custom software development Noida",
      "offshore custom software development",
      "enterprise custom software development",
    ],
    capabilities: [
      "Business Analysis",
      "Solution Architecture",
      "Custom Web Software",
      "Custom Mobile Software",
      "Workflow Automation",
      "API & Integrations",
      "UI/UX Engineering",
      "QA & Release Management",
      "Support & Evolution",
    ],
    accent: "from-sky-500/25 to-blue-600/20",
    featured: false,
    problem: {
      title: "Off-the-shelf tools force your business to bend.",
      body: "Generic products create workarounds, shadow IT, and integrations that never quite fit how your teams operate.",
    },
    opportunity: {
      title: "Software shaped around how you win.",
      body: "We design and build custom systems that match your workflows, data, and customer journeys — ready to scale.",
    },
    outcomes: [
      "Software aligned to real business processes",
      "Cleaner integrations across your stack",
      "Faster internal operations and fewer manual steps",
      "A platform you own and can evolve",
    ],
    faqs: [
      {
        question: "How is custom software different from buying SaaS?",
        answer:
          "Custom software fits your unique processes and integrations. We help you decide build-vs-buy, then deliver only when custom creates lasting advantage.",
      },
      {
        question: "Do you provide long-term maintenance?",
        answer:
          "Yes. We offer support, enhancement, and dedicated team models so your product keeps improving after launch.",
      },
      {
        question: "Where is Maxridge based?",
        answer:
          "Maxridge is based in Sector 63, Noida (Delhi NCR), India, and partners with clients worldwide.",
      },
    ],
  },
  {
    id: "web",
    number: "08",
    title: "Web Application Development",
    slug: "web-app-development",
    href: "/services/web-app-development",
    short:
      "Fast, secure, scalable web apps — from customer portals to complex SaaS products.",
    description:
      "Modern web application development with React, Next.js, and robust backends — performance, SEO, accessibility, and cloud readiness included.",
    seoTitle: "Web Application Development Company",
    seoDescription:
      "Web application development by Maxridge: React, Next.js, and full-stack engineering for SaaS, portals, dashboards, and high-performance business web apps.",
    keywords: [
      "web application development company",
      "React development company",
      "Next.js development services",
      "SaaS web app development",
      "full stack web development",
    ],
    capabilities: [
      "React / Next.js Apps",
      "Progressive Web Apps",
      "Admin Dashboards",
      "Customer Portals",
      "SaaS Frontends",
      "API-driven Architecture",
      "Performance & SEO",
      "Accessibility",
      "Cloud Hosting Setup",
    ],
    accent: "from-cyan-500/25 to-indigo-500/20",
    featured: false,
    problem: {
      title: "Slow, brittle web products hurt conversion and trust.",
      body: "Outdated frontends and tangled backends create poor UX, security risk, and expensive change cycles.",
    },
    opportunity: {
      title: "Ship web products users actually prefer.",
      body: "We build modern web applications with clean UX, solid APIs, and infrastructure that supports continuous delivery.",
    },
    outcomes: [
      "Higher-performing customer-facing web apps",
      "Faster iteration with modern frontend stacks",
      "Better SEO and Core Web Vitals foundations",
      "Secure authentication and role-based access",
    ],
    faqs: [
      {
        question: "Which frontend frameworks do you recommend?",
        answer:
          "We commonly use React and Next.js for product speed, SEO, and maintainability, while selecting backends that fit your domain.",
      },
      {
        question: "Can you rebuild an existing web app?",
        answer:
          "Yes. We modernize incrementally or rebuild when the architecture can no longer support growth safely.",
      },
      {
        question: "Do you handle SEO for web products?",
        answer:
          "Yes. We implement technical SEO foundations including metadata, performance, structured data, and crawl-friendly routing.",
      },
    ],
  },
  {
    id: "mobile",
    number: "09",
    title: "Mobile App Development",
    slug: "mobile-app-development",
    href: "/services/mobile-app-development",
    short:
      "iOS and Android apps that feel native, sync reliably, and scale with your product roadmap.",
    description:
      "Mobile app development for startups and enterprises — React Native and native-quality experiences with secure APIs, analytics, and store-ready delivery.",
    seoTitle: "Mobile App Development Company",
    seoDescription:
      "Mobile app development company Maxridge builds iOS and Android apps with React Native and production backends — secure, scalable, and store-ready.",
    keywords: [
      "mobile app development company",
      "React Native development",
      "iOS Android app development",
      "hire mobile app developers",
      "cross platform app development",
    ],
    capabilities: [
      "React Native Apps",
      "iOS & Android Delivery",
      "Mobile UX Engineering",
      "Push & Notifications",
      "Offline-first Patterns",
      "Secure Auth",
      "App Store Release",
      "Mobile Analytics",
      "API Integration",
    ],
    accent: "from-violet-500/25 to-fuchsia-500/20",
    featured: false,
    problem: {
      title: "Mobile products that launch late and feel unfinished.",
      body: "Fragmented platforms, weak backend design, and poor release discipline delay market feedback.",
    },
    opportunity: {
      title: "Launch mobile experiences customers return to.",
      body: "We build cross-platform or targeted mobile apps with solid APIs, analytics, and a path to continuous improvement.",
    },
    outcomes: [
      "Faster time-to-store for MVP and v1 apps",
      "Consistent UX across iOS and Android",
      "Secure user data and session handling",
      "Clear roadmap for post-launch growth",
    ],
    faqs: [
      {
        question: "Do you build native or cross-platform apps?",
        answer:
          "We recommend based on UX needs, timeline, and team skills. React Native is often ideal for speed without sacrificing quality.",
      },
      {
        question: "Can you integrate with our existing backend?",
        answer:
          "Yes. We integrate with existing APIs or design a mobile-ready backend when needed.",
      },
      {
        question: "Do you support App Store and Play Store releases?",
        answer:
          "Yes. We handle store preparation, release pipelines, and post-launch monitoring support.",
      },
    ],
  },
  {
    id: "mvp",
    number: "10",
    title: "MVP Development",
    slug: "mvp-development",
    href: "/services/mvp-development",
    short:
      "Validate ideas fast with production-minded MVPs — not throwaway prototypes.",
    description:
      "MVP development for founders and product teams: scoped features, clean architecture, and a path from first users to a scalable product.",
    seoTitle: "MVP Development Company for Startups",
    seoDescription:
      "MVP development company Maxridge helps startups and product teams launch validated MVPs fast — with scalable architecture, AI options, and clear next-step roadmaps.",
    keywords: [
      "MVP development company",
      "startup MVP development",
      "build MVP software",
      "product development partner",
      "MVP development India",
    ],
    capabilities: [
      "Product Discovery",
      "Scope & Prioritization",
      "Rapid Prototyping",
      "MVP Architecture",
      "Core Feature Build",
      "Analytics Setup",
      "Launch Support",
      "Iteration Roadmap",
    ],
    accent: "from-orange-400/25 to-amber-500/20",
    featured: false,
    problem: {
      title: "Overbuilding before learning what users need.",
      body: "Teams burn runway on features that don’t validate demand or create a maintainable foundation.",
    },
    opportunity: {
      title: "Launch lean, learn fast, scale with confidence.",
      body: "We help you ship a focused MVP with architecture that can grow — so early traction turns into a real product.",
    },
    outcomes: [
      "Faster path from idea to first users",
      "Clear product scope and success metrics",
      "Architecture that won’t force a rewrite",
      "Evidence-based roadmap after launch",
    ],
    faqs: [
      {
        question: "How quickly can an MVP be launched?",
        answer:
          "Many focused MVPs ship in 4–12 weeks depending on complexity, integrations, and decision speed. We confirm timeline during discovery.",
      },
      {
        question: "Will the MVP be throwaway code?",
        answer:
          "No. We build production-minded foundations so successful MVPs can evolve without starting over.",
      },
      {
        question: "Do you help with AI-powered MVPs?",
        answer:
          "Yes. We can include copilots, RAG, or automation when they create clear user value in the first release.",
      },
    ],
  },
  {
    id: "dedicated",
    number: "11",
    title: "Hire Dedicated Developers",
    slug: "hire-dedicated-developers",
    href: "/services/hire-dedicated-developers",
    short:
      "Senior engineers who join your roadmap — dedicated capacity without slow hiring cycles.",
    description:
      "Hire dedicated developers and managed squads from Maxridge: React, Node, Python, AI, cloud, and full-stack talent with transparent ownership and sprint delivery.",
    seoTitle: "Hire Dedicated Developers & Engineering Teams",
    seoDescription:
      "Hire dedicated developers from Maxridge — React, Python, AI, cloud, and full-stack engineers as dedicated teams or team extension with predictable delivery.",
    keywords: [
      "hire dedicated developers",
      "hire software developers",
      "dedicated development team",
      "dedicated development team India",
      "team extension services",
      "hire React developers",
      "hire Python developers India",
      "offshore dedicated developers",
      "outsource software development India",
    ],
    capabilities: [
      "Team Extension",
      "Dedicated Squads",
      "Full-Stack Engineers",
      "AI / ML Engineers",
      "Cloud & DevOps Engineers",
      "Tech Lead Oversight",
      "Flexible Scaling",
      "Knowledge Transfer",
    ],
    accent: "from-emerald-400/25 to-teal-500/20",
    featured: false,
    problem: {
      title: "Hiring is slow when delivery cannot wait.",
      body: "Critical roadmap items stall while recruiting, onboarding, and ramp-up consume months.",
    },
    opportunity: {
      title: "Add productive capacity in weeks, not quarters.",
      body: "Get dedicated engineers or pods that integrate with your tools, standups, and quality bar — ready to own outcomes.",
    },
    outcomes: [
      "Faster capacity without permanent headcount risk",
      "Senior talent matched to your stack",
      "Clear communication and sprint rhythm",
      "Easy scale-up or scale-down as priorities shift",
    ],
    faqs: [
      {
        question: "How fast can dedicated developers start?",
        answer:
          "After requirements alignment, onboarding commonly begins within 1–2 weeks depending on skill profile and availability.",
      },
      {
        question: "Who manages the engineers day to day?",
        answer:
          "You can lead priorities (team extension) or engage a Maxridge-led pod with a tech lead for shared delivery ownership.",
      },
      {
        question: "Can we replace an engineer if needed?",
        answer:
          "Yes. We work to resolve fit issues quickly and provide replacement options so delivery stays protected.",
      },
    ],
  },
  {
    id: "integration",
    number: "12",
    title: "System Integration & Automation",
    slug: "system-integration",
    href: "/services/system-integration",
    short:
      "Connect ERP, CRM, data, APIs, and AI agents into one reliable operating fabric.",
    description:
      "Enterprise system integration and workflow automation — API-first architecture that eliminates silos and prepares your stack for AI-driven operations.",
    seoTitle: "System Integration & Workflow Automation Services",
    seoDescription:
      "System integration services by Maxridge: connect ERP, CRM, legacy apps, APIs, data platforms, and AI agents for seamless enterprise workflows.",
    keywords: [
      "system integration services",
      "enterprise integration",
      "API integration services",
      "workflow automation",
      "ERP CRM integration",
    ],
    capabilities: [
      "API Development",
      "ERP / CRM Integration",
      "Legacy System Integration",
      "Event-driven Architecture",
      "Workflow Automation",
      "iPaaS Patterns",
      "Data Synchronization",
      "AI Tool Integration",
      "Monitoring & Alerts",
    ],
    accent: "from-blue-500/25 to-cyan-400/20",
    featured: false,
    problem: {
      title: "Systems that don’t talk create expensive manual work.",
      body: "Disconnected tools force copy-paste operations, delayed reporting, and broken customer journeys.",
    },
    opportunity: {
      title: "Make your enterprise operate as one system.",
      body: "We integrate platforms with durable APIs and automation so data flows, teams move faster, and AI can act safely.",
    },
    outcomes: [
      "Fewer manual handoffs between systems",
      "Real-time or near-real-time data sync",
      "Automation that reduces operational cost",
      "Cleaner foundation for AI agents and copilots",
    ],
    faqs: [
      {
        question: "Which systems can you integrate?",
        answer:
          "ERP, CRM, billing, support desks, data warehouses, custom apps, cloud services, and AI tools via APIs and event streams.",
      },
      {
        question: "Do you support AI agent integration into existing tools?",
        answer:
          "Yes. We connect LLMs and agents to enterprise systems with authentication, permissions, and auditability.",
      },
      {
        question: "How do you avoid fragile point-to-point integrations?",
        answer:
          "We prefer API-first and event-driven patterns with clear contracts, monitoring, and ownership — not brittle one-off scripts.",
      },
    ],
  },
];

export const allServices: Service[] = [...services, ...specialtyServices];
export const featuredServices = services.filter((s) => s.featured);
